import {setContext} from "@apollo/client/link/context";
import {Client, createClient} from "graphql-ws";
import {store} from "../../Store/index.js";
import {GetUserInfoJWTString, SendUserJWTToMGL} from "../AutoRuns/UserInfoCheck.js";
import {RunInAction} from "web-vcore";
import {ApolloClient, ApolloLink, DefaultOptions, from, gql, HttpLink, split} from "@apollo/client";
import type {FetchResult} from "@apollo/client/link";
import {NormalizedCacheObject} from "@apollo/client/cache";
import {CombinedGraphQLErrors} from "@apollo/client/errors";
import {getMainDefinition} from "@apollo/client/utilities/index.js";
import {GraphQLWsLink} from "@apollo/client/link/subscriptions/index.js";
import {onError} from "@apollo/client/link/error/index.js";
import {VoidCache} from "./Apollo/VoidCache.js";
import {GetServerURL, GetServerURL_Options} from "../General/Domains.js";
import {AssertWarn, Timer} from "js-vextensions";

export function GetPageUrl_WithWebpackToK8sRetargeting() {
	const newUrl = new URL(window.location.href);
	// if on localhost, but one of the webpack ports, change the port to target the k8s entry-point (webpack knows nothing of k8s pods, so we need to retarget to them)
	if (newUrl.hostname == "localhost" && (newUrl.port == "4101" || newUrl.port == "4131")) {
		newUrl.port = newUrl.protocol == "https:" ? "4102" : "4100";
	}
	return newUrl.href;
}

/** Flags ws failures caused by an untrusted TLS cert. Local clusters have no cert-manager
 * (Tilt/CertManager.star is remote-only), so nginx serves its self-signed `cafe.example.com` cert. */
export function DetectAndFlagUntrustedWSCert() {
	if (store.main.wsCertUntrusted) return; // already flagged; don't thrash
	if (store.wvc.webSocketConnected) return; // connected fine, so no cert problem

	let wsUrl: string;
	try {
		wsUrl = GRAPHQL_URL.replace(/^http/, "ws");
	} catch {
		return;
	}

	let parsed: URL;
	try {
		parsed = new URL(wsUrl);
	} catch {
		return;
	}

	const isSecure = parsed.protocol == "wss:";
	// the local nginx gateway exposes https on 4102 (see NGINX_Gateway.star); this is the only case where
	// the "accept the self-signed cert" step is expected
	const isLocalGateway = (parsed.hostname == "localhost" || parsed.hostname == "127.0.0.1") && parsed.port == "4102";
	if (!isSecure || !isLocalGateway) return;

	// the banner links here, so it must be a page-openable https:// origin (not wss://)
	const origin = `https://${parsed.host}`;
	console.warn(`WebSocket to ${wsUrl} never opened; assuming an untrusted local-cluster certificate. Offering the "accept certificate" link.`);
	RunInAction("DetectAndFlagUntrustedWSCert", ()=>{
		store.main.wsCertUntrusted = true;
		store.main.wsCertUntrustedURL = origin;
	});
}

export function GetWebServerURL(subpath: string, preferredServerOrigin?: string, opts?: GetServerURL_Options) {	opts = {...{claimedClientURL: preferredServerOrigin ?? GetPageUrl_WithWebpackToK8sRetargeting(), restrictToRecognizedHosts: false}, ...opts};
	if (DB == "prod") return `https://ad0wof.nodes.c1.or1.k8s.ovh.us/${subpath.replace(/^\//, "")}`; // if connecting to prod web-server, be consistent and always use the OVH origin domain
	return GetServerURL("web-server", subpath, opts);
}
export function GetAppServerURL(subpath: string, preferredServerOrigin?: string, opts?: GetServerURL_Options): string {
	opts = {...{claimedClientURL: preferredServerOrigin ?? GetPageUrl_WithWebpackToK8sRetargeting(), restrictToRecognizedHosts: false}, ...opts};
	if (DB == "prod") return `https://ad0wof.nodes.c1.or1.k8s.ovh.us/app-server/${subpath.replace(/^\//, "")}`; // if connecting to prod app-server, be consistent and always use the OVH origin domain
	return GetServerURL("app-server", subpath, opts);
}
export function GetMonitorURL(subpath: string, preferredServerOrigin?: string, opts?: GetServerURL_Options): string {
	opts = {...{claimedClientURL: preferredServerOrigin ?? GetPageUrl_WithWebpackToK8sRetargeting(), restrictToRecognizedHosts: false}, ...opts};
	if (DB == "prod") return `https://ad0wof.nodes.c1.or1.k8s.ovh.us/monitor/${subpath.replace(/^\//, "")}`; // if connecting to prod monitor, be consistent and always use the OVH origin domain
	return GetServerURL("monitor", subpath, opts);
}

const GRAPHQL_URL = GetAppServerURL("/graphql");
// for graphql/websocket connections, bypass cloudflare-cdn (ie. debates.app) and connect directly to the server cluster (ie. debating.app)
// (the cdn is nice for caching the static files, but for live data transfer, it has no value -- so direct is better, eg. to avoid cloudflare's websocket timeouts)
//const GRAPHQL_URL = GetAppServerURL("/graphql", "debating.app");

export let httpLink: HttpLink;
export let wsClient: Client;
export let wsClient_connectCount = 0; // export, so can check with console command
export let wsLink: GraphQLWsLink;
export let link: ApolloLink;
export let link_withErrorHandling: ApolloLink;
export let apolloClient: ApolloClient;

export const wsClient_onConnectedListeners = [] as (()=>void)[];
export const wsClient_onDisconnectedListeners = [] as (()=>void)[];

export function InitApollo() {
	InitApolloLink();
	InitApolloClient();
}
export function InitApolloLink() {
	httpLink = new HttpLink({
		uri: GRAPHQL_URL,
		// allows cookies to be sent with "graphql" calls (eg. for passing passportjs session-token with mutation/command calls)
		fetchOptions: {
			credentials: "include",
		},
	});

	// terminate+dispose old client, if exists (ie. if being recreated/replaced)
	if (wsClient) {
		wsClient.terminate();
		wsClient.dispose();
	};
	let thisWSClient: Client|n; // declare it separately/first, so call to thisWSClient_isLatest() [from "connected" callback] doesn't error
	const thisWSClient_isLatest = ()=>wsClient == thisWSClient;
	wsClient = thisWSClient = createClient({
		url: GRAPHQL_URL.replace(/^http/, "ws"),
		/*reconnect:
			(startURL.GetQueryVar("ws_rc") == "0" ? false : null) ?? // for testing
			true,*/
		retryAttempts: Number.MAX_SAFE_INTEGER,
		// use non-lazy mode: connect now (rather than at time of first data-sub), and never try to close the connection for "inactivity"
		// (immediacy lowers chance of auth-data sending after data-subs, ie. churn; no-closing avoids DC->RC->ClearCache->NoSubs->DC cycle)
		lazy: false,
		// if websocket is going to be closed for inactivity/no-subscriptions, do it only if that state holds for 3+ seconds
		// (this shouldn't actually be needed given we have lazy:false now, but start out with it till RC-cycle issue is confirmed solved)
		lazyCloseTimeout: 3000,
		on: {
			// could also detect general web dc/rc (https://developer.mozilla.org/en-US/docs/Web/API/Navigator/Online_and_offline_events), but doesn't seem necessary
			connected: ()=>{
				if (!thisWSClient_isLatest()) return;
				wsClient_connectCount++;
				console.log(`WebSocket connected. (count: ${wsClient_connectCount})`);
				RunInAction("wsClient.onConnected", ()=>store.wvc.webSocketConnected = true);

				// right at start, we need to associate our user-id with our websocket-connection (so server can grant access to user-specific data)
				AttachUserJWTToWebSocketConnection().then(()=>{
					wsClient_onConnectedListeners.forEach(a=>a());
				});
			},
			/*()=>{
				wsClient_connectCount++;
				console.log(`WebSocket reconnected. (count: ${wsClient_connectCount})`);
				RunInAction("wsClient.onReconnected", ()=>store.wvc.webSocketConnected = true);

				// whenever our web-socket reconnects, we have to authenticate the new websocket connection
				AttachUserJWTToWebSocketConnection();
			},*/
			connecting: ()=>{
				if (!thisWSClient_isLatest()) return;
				console.log("WebSocket connecting.");
				//RunInAction("wsClient.onReconnecting", ()=>store.wvc.webSocketConnected = false);
			},
			opened: ()=>{
				if (!thisWSClient_isLatest()) return;
				console.log("WebSocket opened.");
			},
			closed: (event)=>{
				if (!thisWSClient_isLatest()) return;
				// count DC as part of reconnect process if there's been at least one prior DC, and state was known as disconnected just prior (the WS "disconnects" each time a reconnect attempt is made)
				const asPartOfRC = store.wvc.webSocketLastDCTime != null && !store.wvc.webSocketConnected;
				const closeEvent = event as CloseEvent;
				console.log(`WebSocket disconnected${asPartOfRC ? " (seemingly as part of reconnect)" : ""}. @code:`, closeEvent.code, "@reason:", closeEvent.reason);
				RunInAction("wsClient.onDisconnected", ()=>{
					store.wvc.webSocketConnected = false;
					store.wvc.webSocketLastDCTime = Date.now();
				});
				wsClient_onDisconnectedListeners.forEach(a=>a());
			},
			error: (error: any)=>{
				if (!thisWSClient_isLatest()) return;
				console.error("WebSocket error:", error?.message ?? error);
				// browser hides the TLS failure reason, so this infers it (see DetectAndFlagUntrustedWSCert)
				DetectAndFlagUntrustedWSCert();
			},
		},
	});
	wsLink = new GraphQLWsLink(wsClient);

	// using the ability to split links, you can send data to each link depending on what kind of operation is being sent
	// TODO: maybe remove this splitting, and instead have everything go over the websocket connection, IF it's seen that there's significantly lower latency for that route (relevant for eg. frame-streaming)
	link = split(
		// split based on operation type
		({query})=>{
			const definition = getMainDefinition(query);
			return (
				definition.kind === "OperationDefinition" &&
				definition.operation === "subscription"
			);
		},
		wsLink,
		httpLink,
	);
	const thisLink_withErrorHandling_isLatest = ()=>link_withErrorHandling == thisLink_withErrorHandling;
	const thisLink_withErrorHandling = link_withErrorHandling = from([
		onError(info=>{
			if (!thisLink_withErrorHandling_isLatest()) return;
			// wait a moment before processing, so that call-specific error-handling can be done first (and so it can set the "ignoreInGlobalGQLErrorHandler" field if desired)
			setTimeout(()=>{
				const {error, result, operation, forward} = info;

				if (CombinedGraphQLErrors.is(error)) {
					const gqlErrors = error.errors as ReadonlyArray<{message: string, locations?: unknown, path?: unknown, ignoreInGlobalGQLErrorHandler?: boolean}>;
					if (gqlErrors?.[0]?.["ignoreInGlobalGQLErrorHandler"]) return;

					for (const err of gqlErrors) {
						const {message, locations, path} = err;
						console.error(`[GraphQL error] @message:`, message, "@locations:", locations, "@path:", path, "@response:", result, "@operation", JSON.stringify(operation));
					}
				} else {
					console.error(`[Network error]: ${error.message}`, "@response:", result, "@operation", JSON.stringify(operation));
				}
			});
		}),
		setContext((_, {headers})=>{
			// get the authentication token from local storage if it exists
			const token = GetUserInfoJWTString();
			const finalHeaders = {...headers};
			// only attach the "authorization" header if there's a valid token to place there (otherwise server will error)
			if (token) finalHeaders["authorization"] = `Bearer ${token}`;
			// return the headers to the context so httpLink can read them
			return {
				headers: finalHeaders,
			};
		}),
		link,
	]);
}
export function InitApolloClient() {
	AssertWarn(apolloClient == null, "ApolloClient already initialized! Re-initializing it will cause problems. (eg. due to refs to old instance)");
	apolloClient = new ApolloClient({
		//credentials: "include", // allows cookies to be sent with "graphql" calls (eg. for passing passportjs session-token with mutation/command calls) // this way doesn't work, I think because we send a custom "link"
		link: link_withErrorHandling,
		// replace InMemoryCache with VoidCache, because even a "not used" InMemoryCache has significant overhead, for checking for cache matches and such (>1s over ~25s map-load)
		cache: new VoidCache(),
		// default to not using the cache (it does nothing for subscriptions, and often does *opposite* of what we want for queries [eg. search]; and even when wanted, it's better to explicitly set it)
		defaultOptions: {
			watchQuery: {
				fetchPolicy: "no-cache",
				errorPolicy: "ignore",
			},
			query: {
				fetchPolicy: "no-cache",
				errorPolicy: "all",
			},
		} as DefaultOptions,
	});

	// Websocket doesn't have auth-data attached quite yet (happens in onConnected/onReconnected), but send user-data to MGL immediately anyway.
	// While this has some confusion potential (eg. "user panel shows my name, but commands say auth-data missing"), it's arguably still better than the alternative (causing user to start a redundant sign-in).
	// Note also that, once the auth-data is attached, mobx-graphlink will clear its cache and redo its queries; in most cases this happens fast enough to not be annoying, but is worth noting.
	SendUserJWTToMGL();
}
export function AttachApolloClientLink() {
	apolloClient.setLink(link_withErrorHandling);
}

// todo: ensure that this request gets sent before any others, on the websocket connection (else those ones will fail)
export async function AttachUserJWTToWebSocketConnection() {
	// associate user-info jwt to websocket-connection, by calling the `signInAttach` endpoint
	const fetchResult_subscription = apolloClient.subscribe({
		query: gql`
			subscription($input: SignInAttachInput!) {
				signInAttach(input: $input) {
					success
				}
			}
		`,
		variables: {input: {
			jwt: GetUserInfoJWTString(),
		}},
		//errorPolicy: "ignore",
	});
	const fetchResult = await new Promise<ApolloClient.SubscribeResult<any>>(resolve=>{
		const subscription = fetchResult_subscription.subscribe({
			next: result=>{
				subscription.unsubscribe(); // unsubscribe as soon as first (and only) result is received
				resolve(result);
			},
			// By providing an error-handler function, we prevent apollo from turning the gql-error into a "full-fledged error" -- which would trigger an on-screen error-message.
			// In this case, we don't want it to trigger an on-screen error-message, because the verification failure is likely benign. For example, it can happen when changing the "?db=[dev/prod]" url-flag.
			error: (err: Error & {graphQLErrors?: Array<{message?: string, locations?: unknown, path?: unknown}>})=>{
				// tell global gql-error-handler (seen above) to not handle this error; that way we can provide a more helpful error-message here, without two errors being logged
				const innerError = err.graphQLErrors?.[0]; // ts-def lies; graphQLErrors is null in some cases!
				if (innerError) innerError["ignoreInGlobalGQLErrorHandler"] = true;

				console.error(`Error attaching auth-data jwt to websocket connection; you'll likely need to sign-in again. This is likely benign, eg. when changing the "?db=[dev/prod]" url-flag. @error:`, err);
				//if (innerError) SendErrorToSentry(innerError);
			},
		});
	});
	console.log("Tried attaching auth-data jwt to websocket connection. @success:", fetchResult.data.signInAttach.success);

	SendUserJWTToMGL();
}

// The WebSocket connection apparently often does not realize when it has been disconnected. To force detection of this, just use a timer that creates a new subscription (then immediately drops it), every 5s.
// (We could also verify that we get the pong response, and if not manually close the websocket, but that appears to not be necessary; simply making the subscription attempt makes the system realize the websocket is closed, somehow.)
export const websocketConfirmAliveTimer_subFuncCalls = [] as {date: number, funcName: string, args: any[]}[];
export const websocketConfirmAliveTimer = new Timer(5000, ()=>{
	if (wsClient == null) return;
	const closeSub = wsClient.subscribe({query: "subscription { _ping { pong } }"}, {
		next: result=>{
			websocketConfirmAliveTimer_subFuncCalls.push({date: Date.now(), funcName: "next", args: [result]});
			//console.log("Ping", result);
			closeSub();
		},
		complete: ()=>{
			websocketConfirmAliveTimer_subFuncCalls.push({date: Date.now(), funcName: "complete", args: []});
			//console.log("Complete");
			closeSub();
		},
		error: error=>{
			websocketConfirmAliveTimer_subFuncCalls.push({date: Date.now(), funcName: "error", args: [error]});
			//console.error("Error", error);
			closeSub();
		},
	});
}).Start();